// src/components/dashboards/charts/EventTimeline.tsx
// Multi-line chart: monthly event counts by type across all domains.

import React from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import ReactECharts from "echarts-for-react";
import useChartTheme from "./useChartTheme";
import { EventsTimelineData } from "hooks/useAnalytics";
import { useTranslation } from "../../../i18n";

interface Props {
  data: EventsTimelineData | null;
  loading: boolean;
}

const EventTimeline: React.FC<Props> = ({ data, loading }) => {
  const { baseAxis, baseTooltip, baseGrid, seriesColors, textPrimary, chartHeight } = useChartTheme();
  const { t } = useTranslation();

  if (loading || !data) {
    return <Skeleton variant="rounded" height={chartHeight} sx={{ borderRadius: 2 }} />;
  }

  if (!data.months || data.months.length === 0) {
    return (
      <Box sx={{ height: chartHeight, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="body2" color="text.secondary">
          {t("dashboard.analytics.no_timeline_data", "No event timeline data available")}
        </Typography>
      </Box>
    );
  }

  const option = {
    title: {
      text: t("dashboard.analytics.event_timeline", "Clinical Events Timeline"),
      textStyle: { color: textPrimary, fontSize: 14, fontWeight: 600 },
      left: 0,
      top: 0,
    },
    tooltip: { ...baseTooltip, trigger: "axis" },
    legend: {
      data: data.series.map((s) => s.name),
      textStyle: { color: textPrimary, fontSize: 10 },
      top: 28,
      right: 0,
      type: "scroll",
    },
    grid: { ...baseGrid, top: "22%" },
    xAxis: {
      ...baseAxis,
      type: "category",
      data: data.months,
      axisLabel: { ...baseAxis.axisLabel, rotate: data.months.length > 12 ? 35 : 0 },
    },
    yAxis: { ...baseAxis, type: "value" },
    series: data.series.map((s, i) => ({
      name: s.name,
      type: "line",
      data: s.data,
      smooth: true,
      symbol: "circle",
      symbolSize: 6,
      lineStyle: { width: 2, color: seriesColors[i % seriesColors.length] },
      itemStyle: { color: seriesColors[i % seriesColors.length] },
      areaStyle: { color: `${seriesColors[i % seriesColors.length]}15` },
    })),
  };

  return (
    <Box sx={{ width: "100%", height: chartHeight }}>
      <ReactECharts option={option} style={{ width: "100%", height: "100%" }} opts={{ renderer: "canvas" }} />
    </Box>
  );
};

export default EventTimeline;
